<template>
  <section class="price-card">
    <div class="price-head">
      <div class="price-figures">
        <span class="price-title">{{ title }}</span>
        <div class="price-now">
          <span class="price-value">{{ format(current) }}</span>
          <span v-if="move" class="price-move" :class="move.tone">{{ move.text }}</span>
        </div>
        <span v-if="note" class="price-note">{{ note }}</span>
      </div>
      <div class="period-row" role="group" aria-label="Chart range">
        <button
          v-for="p in availablePeriods"
          :key="p.key"
          type="button"
          class="period-pill"
          :class="{ active: p.key === period }"
          :aria-pressed="p.key === period"
          @click="period = p.key"
        >
          {{ p.label }}
        </button>
      </div>
    </div>

    <div class="chart-wrap" @mousemove="onMove" @mouseleave="hoverIndex = null">
      <svg class="chart-svg" viewBox="0 0 300 180" preserveAspectRatio="none" role="img" :aria-label="`${title}, ${move ? move.text : ''}`">
        <defs>
          <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" :stop-color="lineColour" stop-opacity="0.22" />
            <stop offset="100%" :stop-color="lineColour" stop-opacity="0" />
          </linearGradient>
        </defs>
        <line
          v-for="(g, i) in gridlines"
          :key="i"
          x1="0"
          :y1="g"
          x2="300"
          :y2="g"
          stroke="#EEF1F6"
          stroke-width="1"
          vector-effect="non-scaling-stroke"
        />
        <path :d="fillPath" :fill="`url(#${gradientId})`" />
        <path
          :d="linePath"
          fill="none"
          :stroke="lineColour"
          stroke-width="2.5"
          vector-effect="non-scaling-stroke"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <div class="y-labels">
        <span v-for="(lbl, i) in yLabels" :key="i" class="y-label" :style="{ top: lbl.top + '%' }">{{ lbl.text }}</span>
      </div>
      <template v-if="hoverPoint">
        <div class="hover-line" :style="{ left: hoverPoint.leftPct + '%' }"></div>
        <div class="hover-dot" :style="{ left: hoverPoint.leftPct + '%', top: hoverPoint.topPct + '%', background: lineColour }"></div>
        <div class="tooltip" :style="{ left: hoverPoint.leftPct + '%' }">
          {{ format(hoverPoint.value) }} · {{ dayLabel(hoverPoint.date) }}
        </div>
      </template>
    </div>

    <div class="x-labels">
      <span>{{ visible.length ? dayLabel(visible[0].date) : "" }}</span>
      <span>{{ visible.length ? dayLabel(visible[visible.length - 1].date) : "" }}</span>
    </div>

    <!-- A long straight line reads as broken data, so say why it is flat. -->
    <p v-if="flatSince" class="flat-note">
      No change since {{ dayLabel(flatSince) }}<template v-if="flatNote"> · {{ flatNote }}</template>
    </p>

    <slot />
  </section>
</template>

<script>
import { money } from "@/utils/exchange.js";

const W = 300;
const H = 180;
const PAD_TOP = 14;
const PAD_BOTTOM = 12;
const PLOT_H = H - PAD_TOP - PAD_BOTTOM;
const DAY_MS = 24 * 60 * 60 * 1000;
/** How long a price has to sit still before the chart says so. */
const FLAT_AFTER_DAYS = 14;

const PERIODS = [
  { key: "1M", label: "1M", days: 30 },
  { key: "3M", label: "3M", days: 91 },
  { key: "1Y", label: "1Y", days: 365 },
  { key: "ALL", label: "All", days: null },
];

let instance = 0;

/**
 * A token's price over time, from its daily closes. Same drawing as the portfolio growth
 * chart, with a range picker; ranges longer than the history are not offered.
 */
export default {
  name: "TokenPriceChart",
  props: {
    // [{ date: Date, value: number }], oldest first
    closes: { type: Array, default: () => [] },
    currency: { type: String, default: "NGN" },
    title: { type: String, default: "Price" },
    note: { type: String, default: "" },
    decimals: { type: Number, default: 2 },
    // The price right now, which can be ahead of the last nightly close.
    currentValue: { type: Number, default: 0 },
    // Why the price can sit still, shown after "No change since …" (an NGX stock only moves on
    // days when enough shares trade).
    flatNote: { type: String, default: "" },
  },
  data() {
    instance += 1;
    return { period: "1Y", hoverIndex: null, gradientId: `token-price-grad-${instance}` };
  },
  computed: {
    spanDays() {
      if (this.closes.length < 2) return 0;
      return (this.closes[this.closes.length - 1].date - this.closes[0].date) / DAY_MS;
    },
    availablePeriods() {
      // A range only shows when the history covers most of it; "All" always does.
      return PERIODS.filter((p) => p.days === null || this.spanDays >= p.days * 0.8);
    },
    visible() {
      const p = PERIODS.find((x) => x.key === this.period) || PERIODS[3];
      if (!p.days || !this.closes.length) return this.closes;
      const cutoff = this.closes[this.closes.length - 1].date.getTime() - p.days * DAY_MS;
      return this.closes.filter((c) => c.date.getTime() >= cutoff);
    },
    // The first day of an unchanged run at the end of the history, when the run is two weeks or
    // more; null while the price is moving.
    flatSince() {
      const n = this.closes.length;
      if (n < 2) return null;
      const last = this.closes[n - 1];
      let i = n - 1;
      while (i > 0 && Math.abs(this.closes[i - 1].value - last.value) <= Math.abs(last.value) * 1e-9) i -= 1;
      const start = this.closes[i];
      return last.date - start.date >= FLAT_AFTER_DAYS * DAY_MS ? start.date : null;
    },
    current() {
      return this.currentValue || this.visible[this.visible.length - 1]?.value || 0;
    },
    move() {
      const first = this.visible[0]?.value;
      if (!first || this.visible.length < 2) return null;
      const n = (this.current - first) / first;
      const arrow = n > 0 ? "▲" : n < 0 ? "▼" : "";
      return { text: `${arrow} ${Math.abs(n * 100).toFixed(2)}%`.trim(), tone: n > 0 ? "up" : n < 0 ? "down" : "" };
    },
    lineColour() {
      return this.move?.tone === "down" ? "#EF4444" : "#4E84FF";
    },
    bounds() {
      const vals = this.visible.map((c) => c.value);
      let min = Math.min(...vals);
      let max = Math.max(...vals);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
      if (min === max) {
        const pad = Math.abs(min) * 0.01 || 1;
        min -= pad;
        max += pad;
      }
      return { min, max };
    },
    points() {
      const n = this.visible.length;
      const { min, max } = this.bounds;
      return this.visible.map((c, i) => ({
        x: n === 1 ? 0 : (i / (n - 1)) * W,
        y: PAD_TOP + (1 - (c.value - min) / (max - min)) * PLOT_H,
        value: c.value,
        date: c.date,
      }));
    },
    linePath() {
      return this.points.map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`).join(" ");
    },
    fillPath() {
      if (!this.points.length) return "";
      const last = this.points[this.points.length - 1];
      return `${this.linePath} L ${last.x} ${H} L 0 ${H} Z`;
    },
    gridlines() {
      return [0, 0.5, 1].map((r) => PAD_TOP + r * PLOT_H);
    },
    yLabels() {
      const { min, max } = this.bounds;
      return [0, 0.5, 1].map((r) => ({
        text: this.format(max - r * (max - min)),
        top: ((PAD_TOP + r * PLOT_H) / H) * 100,
      }));
    },
    hoverPoint() {
      const p = this.hoverIndex == null ? null : this.points[this.hoverIndex];
      if (!p) return null;
      return { leftPct: (p.x / W) * 100, topPct: (p.y / H) * 100, value: p.value, date: p.date };
    },
  },
  watch: {
    availablePeriods: {
      immediate: true,
      handler(periods) {
        if (!periods.some((p) => p.key === this.period)) this.period = periods[periods.length - 2]?.key || "ALL";
      },
    },
  },
  methods: {
    format(value) {
      return money(value, this.currency, this.decimals);
    },
    dayLabel(date) {
      return new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    onMove(e) {
      if (this.visible.length < 2) return;
      const rect = e.currentTarget.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
      this.hoverIndex = Math.round(ratio * (this.visible.length - 1));
    },
  },
};
</script>

<style lang="scss" scoped>
$bg-card: #ffffff;
$border-color: #e2e8f0;
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #94a3b8;
$success: #10b981;
$error: #ef4444;

.price-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 20px;
}

.price-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.price-figures {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.price-title {
  font-size: 12px;
  color: $text-muted;
}

.price-now {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
}

.price-value {
  font-size: 22px;
  color: $text-primary;
}

.price-move {
  font-size: 13px;
  color: $text-secondary;
  &.up { color: $success; }
  &.down { color: $error; }
}

.price-note {
  font-size: 12px;
  color: $text-secondary;
}

.period-row {
  display: flex;
  gap: 6px;
}

.period-pill {
  min-width: 44px;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: #f7f9fd;
  color: #8791a7;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &.active {
    background: #4e84ff;
    color: #ffffff;
  }
}

.chart-wrap {
  position: relative;
  height: 200px;
  width: 100%;

  @media (min-width: 1024px) {
    height: 240px;
  }
}

.chart-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.y-labels {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.y-label {
  position: absolute;
  right: 2px;
  transform: translateY(-50%);
  font-size: 10px;
  color: #b8c0d0;
  background: rgba(255, 255, 255, 0.7);
  padding: 0 2px;
}

.hover-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(78, 132, 255, 0.35);
  transform: translateX(-50%);
  pointer-events: none;
}

.hover-dot {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.tooltip {
  position: absolute;
  top: -2px;
  transform: translateX(-50%);
  padding: 3px 8px;
  border-radius: 6px;
  background: #001240;
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
}

.flat-note {
  margin: 0;
  font-size: 12px;
  color: $text-secondary;
}

.x-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #8791a7;
}
</style>
